import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import {
    CashPriceResponse,
    InsurancePriceResponse,
    InsurancePriceStatusResponse,
    PharmacyDetailResponse,
    PharmacySearchResponse,
} from '@/types/interfaces/pharmacy'

export type PharmacyHoursFilter = 'open_now' | 'open_late' | 'open_24'
export type PharmacyCapabilityFilter = 'specialty' | 'compounding'
export type PharmacySortOption = 'recommended' | 'closest'
export type PharmacyChain = 'walmart'

interface PharmacySearchParams {
    /** Scopes search pricing and the RTK Query cache key to this order's medication. */
    pharmacyOrderId: string
    searchText?: string
    /** Restricts results to the chain's member locations; unsupported values return 422. */
    chain?: PharmacyChain
    page?: number
    perPage?: number
    hours?: PharmacyHoursFilter[]
    capabilities?: PharmacyCapabilityFilter[]
    mailOrder?: boolean
    zipcode?: string
    distance?: number
    sort?: PharmacySortOption
}

interface InsurancePriceParams {
    pharmacyOrderId: string
    // Optional: when omitted, the backend falls back to the patient's preferred
    // pharmacy then their most-recent pharmacy. If neither is set, the response
    // is `{ status: 'no_default_pharmacy' }`.
    ncpdpProviderId?: string
}

interface PharmacyDetailParams {
    pharmacyOrderId: string
    ncpdpProviderId: string
}

interface CashPriceParams {
    pharmacyOrderId: string
    /** When provided, fetches the price specific to this pharmacy. */
    pharmacyNpi?: string
}

export const pharmacyPricingApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        searchPharmacies: builder.query<PharmacySearchResponse, PharmacySearchParams>({
            // Manual URL construction so that array params (e.g. hours[]=open_now&hours[]=open_late)
            // use Rails' bracket notation — RTK Query's default serializer sends repeated keys
            // without brackets, which Rails' strong params with `hours: []` does not parse as an array.
            query: (params) => {
                const search = new URLSearchParams()
                search.set('pharmacy_order_id', params.pharmacyOrderId)
                if (params.searchText) search.set('search_text', params.searchText)
                if (params.chain) search.set('chain', params.chain)
                if (params.page != null) search.set('page', String(params.page))
                if (params.perPage != null) search.set('per_page', String(params.perPage))
                if (params.mailOrder != null) search.set('mail_order', String(params.mailOrder))
                if (params.zipcode) search.set('zipcode', params.zipcode)
                if (params.distance != null) search.set('distance', String(params.distance))
                if (params.sort != null) search.set('sort', params.sort)
                for (const hour of params.hours ?? []) search.append('hours[]', hour)
                for (const capability of params.capabilities ?? []) search.append('capabilities[]', capability)
                const queryString = search.toString()
                return {
                    url: queryString
                        ? `${apiEndpoints.pharmacySearch.search}?${queryString}`
                        : apiEndpoints.pharmacySearch.search,
                }
            },
            providesTags: ['PharmacySearch'],
        }),
        getPharmacyDetails: builder.query<PharmacyDetailResponse, PharmacyDetailParams>({
            query: ({ pharmacyOrderId, ncpdpProviderId }) => ({
                url: apiEndpoints.pharmacyDetail.detail(ncpdpProviderId),
                params: { pharmacy_order_id: pharmacyOrderId },
            }),
            providesTags: ['PharmacyDetail'],
        }),
        getCashPrice: builder.query<CashPriceResponse, CashPriceParams>({
            query: ({ pharmacyOrderId, pharmacyNpi }) => ({
                url: apiEndpoints.pharmacyPricing.cashPrice,
                params: {
                    pharmacy_order_id: pharmacyOrderId,
                    ...(pharmacyNpi ? { pharmacy_npi: pharmacyNpi } : {}),
                },
            }),
            providesTags: (_result, _error, { pharmacyNpi }) => [
                { type: 'PharmacyCashPrice', id: pharmacyNpi ?? 'LIST' },
            ],
        }),
        launchInsurancePriceInquiry: builder.mutation<InsurancePriceResponse, InsurancePriceParams>({
            query: ({ pharmacyOrderId, ncpdpProviderId }) => ({
                url: apiEndpoints.pharmacyPricing.insurance,
                method: 'POST',
                body: {
                    pharmacy_order_id: pharmacyOrderId,
                    ...(ncpdpProviderId ? { ncpdp_provider_id: ncpdpProviderId } : {}),
                },
            }),
            invalidatesTags: ['PharmacyInsurancePrice', 'PharmacyDetail'],
        }),
        getInsurancePriceStatus: builder.query<InsurancePriceStatusResponse, string>({
            query: (inquiryId) => apiEndpoints.pharmacyPricing.insuranceStatus(inquiryId),
            providesTags: ['PharmacyInsurancePrice'],
        }),
    }),
})

export const {
    useSearchPharmaciesQuery,
    useLazySearchPharmaciesQuery,
    useGetPharmacyDetailsQuery,
    useGetCashPriceQuery,
    useLaunchInsurancePriceInquiryMutation,
    useLazyGetInsurancePriceStatusQuery,
} = pharmacyPricingApi
